import type { BadgeVariant } from '@/components/common/Badge';

export type SupplyRequestStatus =
  | 'draft'
  | 'submitted'
  | 'triage'
  | 'pending_approval'
  | 'approved'
  | 'sourcing'
  | 'ordered'
  | 'in_transit'
  | 'delivered'
  | 'partially_fulfilled'
  | 'completed'
  | 'cancelled'
  | 'rejected';

export type SupplyRequestPriority = 'low' | 'medium' | 'high' | 'urgent' | 'critical';

export interface ResourceCategory {
  id: string;
  tenant_id: string | null;
  code: string;
  name: string;
  description: string | null;
  taxonomy: 'NIMS' | 'CHIEFS' | 'CUSTOM';
  parent_id: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface ResourceType {
  id: string;
  tenant_id: string | null;
  category_id: string;
  code: string;
  name: string;
  description: string | null;
  unit_of_measure: string | null;
  nims_type_number: string | null;
  specifications: Record<string, unknown>;
  is_active: boolean;
  category?: {
    id: string;
    code: string;
    name: string;
  };
}

export interface InventoryReservation {
  id: string;
  consumable_id: string;
  consumable_name: string;
  item_code: string;
  quantity_reserved: number;
  quantity_issued: number;
  status: 'reserved' | 'partially_issued' | 'fully_issued' | 'cancelled';
}

export interface SupplyRequestItem {
  id: string;
  supply_request_id: string;
  resource_type_id: string | null;
  item_name: string;
  item_description: string | null;
  quantity_requested: number;
  quantity_approved: number | null;
  quantity_fulfilled: number;
  unit_of_measure: string;
  status: string;
  specifications: Record<string, unknown>;
  substitutes_allowed: boolean;
  substitute_notes: string | null;
  unit_cost: number | null;
  total_cost: number | null;
  sort_order: number;
  resource_type?: ResourceType;
  source_from_inventory?: boolean;
  inventory_fulfilled_quantity?: number;
  inventory_reservations?: InventoryReservation[];
}

export interface SupplyRequestAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size: number;
  storage_key: string;
  uploaded_at: string;
  uploaded_by: string;
  url?: string;
  thumbnail_url?: string;
}

export interface SupplyRequest {
  id: string;
  tenant_id: string;
  incident_id: string | null;
  operational_period_id: string | null;
  request_number: string;
  title: string;
  description: string | null;
  status: SupplyRequestStatus;
  priority: SupplyRequestPriority;
  needed_by: string | null;
  requested_at: string;
  approved_at: string | null;
  fulfilled_at: string | null;
  requested_by: string;
  requesting_unit: string | null;
  assigned_to: string | null;
  approved_by: string | null;
  delivery_location: string | null;
  delivery_address: string | null;
  delivery_coordinates: { lat: number; lng: number } | null;
  delivery_instructions: string | null;
  fulfillment_notes: string | null;
  tracking_numbers: Array<{ carrier?: string; number: string; url?: string }>;
  vendor_info: Record<string, unknown> | null;
  estimated_cost: number | null;
  actual_cost: number | null;
  cost_code: string | null;
  tags: string[];
  attachments: SupplyRequestAttachment[];
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  items: SupplyRequestItem[];
  requested_by_user?: {
    id: string;
    name: string;
    email: string;
  };
  assigned_to_user?: {
    id: string;
    name: string;
    email: string;
  };
  approved_by_user?: {
    id: string;
    name: string;
    email: string;
  };
  incident?: {
    id: string;
    name: string;
    incident_number: string;
  };
}

export interface SupplyRequestHistory {
  id: string;
  supply_request_id: string;
  from_status: SupplyRequestStatus | null;
  to_status: SupplyRequestStatus;
  changed_by: string;
  changed_at: string;
  reason: string | null;
  notes: string | null;
  changed_by_user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateSupplyRequestData {
  incident_id?: string;
  title: string;
  description?: string;
  priority?: SupplyRequestPriority;
  needed_by?: string;
  requesting_unit?: string;
  delivery_location?: string;
  delivery_address?: string;
  delivery_coordinates?: { lat: number; lng: number };
  delivery_instructions?: string;
  estimated_cost?: number;
  cost_code?: string;
  tags?: string[];
  items: CreateSupplyRequestItemData[];
}

export interface CreateSupplyRequestItemData {
  resource_type_id?: string;
  item_name: string;
  item_description?: string;
  quantity_requested: number;
  unit_of_measure?: string;
  specifications?: Record<string, unknown>;
  substitutes_allowed?: boolean;
  substitute_notes?: string;
  unit_cost?: number;
  source_from_inventory?: boolean;
}

export interface PaginatedSupplyRequests {
  data: SupplyRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const statusLabels: Record<SupplyRequestStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  triage: 'Triage',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  sourcing: 'Sourcing',
  ordered: 'Ordered',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  partially_fulfilled: 'Partially Fulfilled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

// Color budget: statuses map onto the severity scale (calm → elevated → warning)
// instead of one arbitrary hue per status. Semantic tokens work in both themes.
export const statusColors: Record<SupplyRequestStatus, string> = {
  draft: 'bg-surface-tertiary text-theme-secondary',
  submitted: 'bg-surface-tertiary text-theme-secondary',
  triage: 'bg-[color:var(--severity-elevated-bg)] text-[color:var(--severity-elevated-text)]',
  pending_approval: 'bg-[color:var(--severity-warning-bg)] text-[color:var(--severity-warning-text)]',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  sourcing: 'bg-surface-tertiary text-theme-secondary',
  ordered: 'bg-surface-tertiary text-theme-secondary',
  in_transit: 'bg-surface-tertiary text-theme-secondary',
  delivered: 'bg-surface-tertiary text-theme-secondary',
  partially_fulfilled: 'bg-[color:var(--severity-warning-bg)] text-[color:var(--severity-warning-text)]',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

// Badge variants for status pills (shared Badge component). Color semantics
// mirror statusColors: calm workflow states are neutral, decision-pending
// states are warning, success/danger keep their severity meaning.
export const statusVariants: Record<SupplyRequestStatus, BadgeVariant> = {
  draft: 'neutral',
  submitted: 'neutral',
  triage: 'warning',
  pending_approval: 'warning',
  approved: 'success',
  sourcing: 'neutral',
  ordered: 'neutral',
  in_transit: 'neutral',
  delivered: 'neutral',
  partially_fulfilled: 'warning',
  completed: 'success',
  cancelled: 'danger',
  rejected: 'danger',
};

export const priorityLabels: Record<SupplyRequestPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
  critical: 'Critical',
};

// Color budget: priority reads on the severity scale (low=calm, medium=elevated,
// high=warning, urgent/critical=escalated) instead of one hue per level.
export const priorityColors: Record<SupplyRequestPriority, string> = {
  low: 'bg-surface-tertiary text-theme-secondary',
  medium: 'bg-[color:var(--severity-elevated-bg)] text-[color:var(--severity-elevated-text)]',
  high: 'bg-[color:var(--severity-warning-bg)] text-[color:var(--severity-warning-text)]',
  urgent: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  critical: 'bg-[color:var(--severity-critical-bg)] text-[color:var(--severity-critical-text)]',
};

// Badge variants for priority pills (shared Badge component). Color semantics
// mirror priorityColors, which is kept for non-pill consumers.
export const priorityVariants: Record<SupplyRequestPriority, BadgeVariant> = {
  low: 'neutral',
  medium: 'neutral',
  high: 'warning',
  urgent: 'orange',
  critical: 'danger',
};
